/*
 * Model domain type — the merged view the UI renders.
 *
 * A model's state arrives on the Models.Subscribe stream as TWO event kinds:
 * a DESCRIPTOR event (intent — name, desired_state, interface, wiring) and a
 * RUNTIME event (live — status, current_state, error, timing). `subscribeModels`
 * folds both halves into one `ModelView` per model id. The SCXML body is NOT
 * here — it's stripped from the stream and fetched on demand via getModelSource.
 *
 * Mirrors `domain/controller.ts`; same library-grade boundary (no React/Lit/DOM).
 */

/** The model-runner's runtime status; 'unknown' until the first runtime event. */
export type ModelStatus =
  | 'starting'
  | 'waiting'
  | 'running'
  | 'paused'
  | 'stopped'
  | 'error'
  | 'unknown';

/** A formal parameter — what the model reads from outside (ModelInput). */
export interface ModelIfaceInput {
  name: string;
  type: string;
  /** ModelInput.Kind: 0 = ANY (symbol OR model var), 1 = SYMBOL, 2 = MODEL_VAR. */
  kind: number;
  optional: boolean;
  isArray: boolean;
}
/** A published variable others can subscribe to (ModelOutput). */
export interface ModelIfaceOutput {
  name: string;
  type: string;
  unit: string;
}
/** A declared command the model accepts (ModelCommand). */
export interface ModelIfaceCommand {
  name: string;
  params: string[];
}
/**
 * A model's declared interface — its public surface, AUTHORED not derived.
 *
 * This mirrors vitesse.v1.ModelInterface field-for-field on purpose: it is
 * round-tripped verbatim on every update (see ModelView.iface), so a field missing
 * here is a field ERASED on the next save. If the proto gains a field, it must be
 * added here in the same change.
 */
export interface ModelIface {
  description: string;
  inputs: ModelIfaceInput[];
  outputs: ModelIfaceOutput[];
  commands: ModelIfaceCommand[];
}

/**
 * One machine of a group, as the runtime reports it (ModelRuntime.machines). `path` is '' for the
 * root, a child's name otherwise, and `sensors/3` for a machine of an array child (Step 4). Two
 * machines can have the same state ids, so a state is always shown with its machine.
 */
export interface ModelMachine {
  path: string;
  /** Comma-separated active state ids; for a faulted machine, where it faulted. */
  state: string;
  /** Why it faulted and stopped (IP-safe, classified by the model-runner), or '' while it runs. */
  fault: string;
  /** A machine of an array whose `enabled` is not true: it has no state and no fault. A stop is not a fault. */
  stopped?: boolean;
}
/** One port of a group's child, with its wiring as the descriptor declares it. */
export interface ModelChildPort {
  name: string;
  /** An input: the entity path or the other child's port it reads, '' when the parent assigns it.
   *  An output: the PLC path it writes, '' when only the group reads it. */
  path: string;
  /** An input fed by a constant (ModelBinding.literal), rendered as text. Absent otherwise. */
  constant?: string;
  /** The port's declared type (BOOL, INT, DINT, REAL, STRING, or a TypeScript type); absent when it has none. */
  type?: string;
  /** An output wired to the PLC: the value it takes when the machine faults, rendered as text. */
  safe?: string;
}
/** A child of a group (ModelChild), with the ports its template declares. */
export interface ModelChildView {
  name: string;
  /** The key of ModelDescriptor.templates it runs. */
  template: string;
  /**
   * An array of machines of the template (Step 4): from 1 to 256, `sensors/0` to `sensors/<count - 1>`.
   * Absent for a single child. A path in its wiring can hold `{i}`, the index, and `{n}`, the index plus 1.
   */
  count?: number;
  /** The template's inputs, and for an array the platform's `enabled` when the wiring feeds it. */
  inputs: ModelChildPort[];
  outputs: ModelChildPort[];
  /**
   * The names that the child's wiring gives to other machines of the group, by name: `{ belt:
   * 'conveyor' }` lets the child read `belt.speed` and send events to `belt`. Not ports, so not in
   * `inputs`. Absent when the child names no machine.
   */
  connections?: Record<string, string>;
}

export interface ModelView {
  id: string;
  /** Human name (descriptor). Falls back to the id. */
  name: string;
  /** Intent: "running" | "stopped" — what the user wants (descriptor). */
  desiredState: string;

  // ── live runtime (defaults until the first runtime event) ────────────────
  status: ModelStatus;
  /** Comma-separated active SCXML state ids (denormalized for cheap display). The root's, for a group. */
  currentState: string;
  /** A group's machines, the root first, each with its state and fault. Empty for a single model. */
  machines: ModelMachine[];
  /** Short, IP-safe error string when status === 'error'. */
  error: string;
  /** Wall-clock ms when the Worker last spawned; 0 when stopped. */
  startedAtMs: number;
  /** Model-time ms elapsed since spawn, excluding paused periods. */
  elapsedMs: number;

  // ── descriptor metadata (SCXML body fetched separately) ──────────────────
  /** The KV revision of the last descriptor event: each save of the descriptor writes a new one (an
   *  edit, a binding, a rename, and a Start or a Stop, which set desiredState). The stream leaves the
   *  SCXML body out, so a page that holds the body reads it again when this changes. 0 until the
   *  first descriptor event. */
  sourceRevision: number;
  /** The model's description (ModelInterface.description) — shown in the header. */
  description: string;
  /** Declared commands the model accepts — the Commands section buttons. */
  commands: string[];
  /** Declared output names — the model's published "return surface" (the
   *  Variables section's chart rows). */
  outputs: string[];
  /** Declared outputs — the model's published "return surface" (count). */
  outputCount: number;
  /** Declared input names (ModelInterface.inputs — the open slots). */
  inputs: string[];
  /** Whether the model declares a ModelInterface. One with none publishes no variable. */
  hasInterface: boolean;
  /**
   * The root's own wiring (ModelDescriptor.inputs and .outputs, Part 4 of the Step 3 plan): each
   * input fed from a path or a constant, and each output wired to the PLC with its safe value, in a
   * child's shape. A model with any of it runs as a group of one. Absent or empty for a model with
   * none: such a model reads nothing from outside.
   */
  wiring?: { inputs: ModelChildPort[]; outputs: ModelChildPort[] };
  /** A group's children (ModelDescriptor.children), in the descriptor's order. Empty for a single
   *  model. The root is the model itself: its document is `scxml`, its ports are `iface`. */
  children: ModelChildView[];
  /** The model's DECLARED interface, kept whole (names + types + units + command
   *  descriptions), not just the flattened name lists above.
   *
   *  LOAD-BEARING: Models.Update replaces the descriptor VERBATIM — the
   *  model-runner kvPut's exactly what it's handed, with no merge and no
   *  re-derivation from the SCXML. So any write that omits the interface ERASES
   *  the model's inputs, outputs and commands. Every writer must round-trip this
   *  back. The flattened lists can't do it: they've already dropped each entry's
   *  type/unit/description. */
  iface?: ModelIface;
}

/** Whether a path is outside the model: a controller symbol, another model's output, or an integration. */
export const isOutsidePath = (path: string): boolean => /^(controllers|models|integrations)\//.test(path);

/**
 * The root's inputs that its own wiring connects to a path, by input name: what the root reads from
 * outside. A constant is not in it. A model with no wiring reads nothing from outside.
 */
export function connectedInputs(m: Pick<ModelView, 'wiring'>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of m.wiring?.inputs ?? []) if (p.path) out[p.name] = p.path;
  return out;
}

/**
 * Every outside path a model reads, by the input that reads it: the root's connected inputs by
 * their names, then each child's as `<child>.<port>`, as the replay names them. A child's input
 * that reads another child stays inside the group, so it is not in it.
 */
export function connectionsOf(m: Pick<ModelView, 'wiring' | 'children'>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, path] of Object.entries(connectedInputs(m))) if (isOutsidePath(path)) out[name] = path;
  for (const c of m.children ?? []) for (const p of c.inputs) if (isOutsidePath(p.path)) out[`${c.name}.${p.name}`] = p.path;
  return out;
}
